/**
 * BirdBrain formatting kit.
 *
 * Single home for number/date formatting across the dashboard.
 * Every formatter accepts null/undefined and renders the app-wide
 * placeholder (MATURING_PLACEHOLDER, '—') instead of throwing or
 * showing a fake 0.
 */
import { MATURING_PLACEHOLDER } from '@/utils/signals'

type Numeric = number | null | undefined
type DateInput = Date | string | number | null | undefined

interface NumberFormatOptions {
  /** Abbreviate thousands/millions: 1234 → "1.2K", 3400000 → "3.4M". */
  compact?: boolean
  /** Fraction digits (default 0 for full numbers, 1 for compact suffixes). */
  decimals?: number
}

function toDate(value: DateInput): Date | null {
  if (value == null) return null
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/** "1.0" → "1" so compact output reads 1K, not 1.0K. */
function trimTrailingZero(s: string): string {
  return s.replace(/\.0+$/, '')
}

function compactNumber(value: number, decimals = 1): string {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${sign}${trimTrailingZero((abs / 1_000_000).toFixed(decimals))}M`
  if (abs >= 1_000) return `${sign}${trimTrailingZero((abs / 1_000).toFixed(decimals))}K`
  // Round below 1K: raw fractional values (e.g. 521.669) must not leak into
  // compact output as "$521.669" (TROI 'Returned' column bug).
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`
}

/** Format a number: "12,345" or compact "12.3K" / "1.2M". */
export function formatNumber(value: Numeric, opts?: NumberFormatOptions): string {
  if (value == null || !isFinite(value)) return MATURING_PLACEHOLDER
  if (opts?.compact) return compactNumber(value, opts.decimals ?? 1)
  const decimals = opts?.decimals ?? 0
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })
}

/** Format a dollar amount: "$12,345" or compact "$12.3K" / "$1.2M". */
export function formatCurrency(value: Numeric, opts?: NumberFormatOptions): string {
  if (value == null || !isFinite(value)) return MATURING_PLACEHOLDER
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (opts?.compact) return `${sign}$${compactNumber(abs, opts.decimals ?? 1)}`
  const decimals = opts?.decimals ?? 0
  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

/** Format a percentage value (already in 0-100 space): 12.34 → "12.3%". */
export function formatPercent(value: Numeric, decimals = 1): string {
  if (value == null || !isFinite(value)) return MATURING_PLACEHOLDER
  return `${value.toFixed(decimals)}%`
}

/**
 * Format a cost in cents as a dollar string.
 * Shows 4 decimal places for sub-cent amounts (< $0.01).
 */
export function formatCost(cents: Numeric): string {
  if (cents == null || !isFinite(cents)) return MATURING_PLACEHOLDER
  const dollars = cents / 100
  if (dollars === 0) return '$0.00'
  if (Math.abs(dollars) < 0.01) return '$' + dollars.toFixed(4)
  return '$' + dollars.toFixed(2)
}

/** Format a date: "Jan 5, 2026". Pass Intl options to override the shape. */
export function formatDate(value: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value)
  if (!d) return MATURING_PLACEHOLDER
  return d.toLocaleDateString('en-US', opts ?? { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Format a date with time: "Jan 5, 2026, 02:14 PM". */
export function formatDateTime(value: DateInput, opts?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value)
  if (!d) return MATURING_PLACEHOLDER
  return d.toLocaleString(
    'en-US',
    opts ?? { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
  )
}

/**
 * Relative time for freshness labels and activity feeds:
 * "just now", "5m ago", "3h ago", "yesterday", "4d ago", "2w ago", "3mo ago",
 * falling back to an absolute date after a year.
 */
export function formatRelative(value: DateInput): string {
  const d = toDate(value)
  if (!d) return MATURING_PLACEHOLDER
  const diffMs = Date.now() - d.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return formatDate(d)
}

/** Format a duration in milliseconds: "850ms", "42s", "2m 5s". */
export function formatDuration(ms: Numeric): string {
  if (ms == null || !isFinite(ms)) return MATURING_PLACEHOLDER
  if (ms < 1000) return `${Math.round(ms)}ms`
  const totalSecs = Math.round(ms / 1000)
  if (totalSecs < 90) return `${totalSecs}s`
  return `${Math.floor(totalSecs / 60)}m ${totalSecs % 60}s`
}

/** Format a cohort week range from its start date: "Dec 1 - Dec 7". */
export function formatWeekDateRange(weekStart: DateInput): string {
  const start = toDate(weekStart)
  if (!start) return MATURING_PLACEHOLDER
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}`
}
